import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import './PayView.css';
import { useToast } from '../shared/ui/Toast/ToastProvider';

const PayView = () => {
    const { id } = useParams();
    const toast = useToast();
    const [linkData, setLinkData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [isKushkiModalOpen, setIsKushkiModalOpen] = useState(false);
    const [kushkiProcessing, setKushkiProcessing] = useState(false);
    const [kushkiSuccess, setKushkiSuccess] = useState(false);
    const [payForm, setPayForm] = useState({ name: '', email: '', card: '', expiry: '', cvc: '' });

    useEffect(() => {
        fetch(`/api/payments/links/${id}`)
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setLinkData(data.link);
                }
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    }, [id]);

    const copyAddress = () => {
        navigator.clipboard.writeText('0X39e...24e2').catch(() => { });
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleKushkiSubmit = async (e) => {
        e.preventDefault();
        setKushkiProcessing(true);
        try {
            const [expiryMonth, expiryYear] = payForm.expiry.split('/');
            const yr = expiryYear ? expiryYear.slice(-2) : '25';

            // 1. Get Token from Kushki
            const tokenReq = await fetch('https://api-uat.kushkipagos.com/card/v1/tokens', {
                method: 'POST',
                headers: {
                    'Public-Merchant-Id': 'a8edd5ffc4644613ae0a36cf48559e29',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    card: {
                        name: payForm.name,
                        number: payForm.card.replace(/\D/g, ''),
                        expiryMonth: expiryMonth,
                        expiryYear: yr,
                        cvv: payForm.cvc
                    },
                    totalAmount: Number(linkData.price),
                    currency: linkData.currency || 'MXN'
                })
            });
            const tokenData = await tokenReq.json();
            if (!tokenData.token) throw new Error(tokenData.message || 'Error al validar tarjeta en el procesador.');

            // 2. Send Token to Backend for Charge
            const req = await fetch('/api/payments/charge', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    payment_link_id: linkData.id,
                    customer_email: payForm.email,
                    customer_name: payForm.name,
                    kushki_token: tokenData.token,
                })
            });
            const res = await req.json();
            if (res.success) {
                setKushkiSuccess(true);
                setTimeout(() => { setIsKushkiModalOpen(false); setKushkiSuccess(false); }, 3000);
            } else {
                toast.error("Error procesando pago: " + res.error);
            }
        } catch (error) {
            console.error(error);
            toast.error("Excepción: " + error.message);
        } finally {
            setKushkiProcessing(false);
        }
    };

    if (loading) {
        return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0c0c0f', color: '#fff' }}>Cargando...</div>;
    }

    if (!linkData) {
        return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0c0c0f', color: '#fc6c6c' }}>Link no encontrado o inactivo</div>;
    }

    return (
        <div className="pay-view-wrapper">
            <div className="pay-view-bg">
                <div className="blob"></div>
                <div className="blob"></div>
                <div className="blob"></div>
                <div className="blob"></div>
            </div>
            <div className="pay-view-blur-overlay"></div>

            <div className="payment-processor">
                {/* Header */}
                <div className="processor-header">
                    <svg width="100" height="14" viewBox="0 0 125 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14.9195 0H3.0805C1.37981 0 0 1.37981 0 3.0805V14.9195C0 16.6219 1.37981 18 3.0805 18H14.9195C16.6219 18 18 16.6202 18 14.9195V3.0805C18 1.37981 16.6202 0 14.9195 0ZM15.1678 4.63764L13.0904 6.73015C13.0465 6.77407 12.9891 6.79771 12.9283 6.79771L7.44624 6.79264C7.31788 6.79264 7.21486 6.89904 7.21486 7.03078V7.67423C7.21486 7.80597 7.31788 7.91237 7.44624 7.91237H11.3154C11.5198 7.91237 11.6245 8.16401 11.4826 8.31432L10.8544 8.98311L10.4727 9.38844L9.85964 10.0403L9.53875 10.368C9.49484 10.4119 9.43573 10.4372 9.37493 10.4372H7.56446H7.39557C7.31451 10.4372 7.25033 10.5048 7.25033 10.5859V10.7598L7.25371 12.572C7.25371 12.6345 7.23006 12.6953 7.18615 12.7409L5.10884 14.8638C4.9636 15.0124 4.71364 14.9077 4.71364 14.6949V10.6922C4.71364 10.5605 4.61062 10.4541 4.48227 10.4541H4.30156C4.16476 10.4541 4.05836 10.3342 4.07187 10.1957L4.25258 8.12854C4.26271 8.00694 4.36405 7.91237 4.48227 7.91237C4.61062 7.91237 4.71364 7.80597 4.71364 7.67423V4.46876C4.71364 4.33702 4.81666 4.23062 4.94502 4.23062H15.0056C15.2117 4.23062 15.3147 4.48902 15.1678 4.63764Z" fill="white" />
                        <path d="M26.9062 13.2388H28.879V6.84803C28.879 6.13642 29.3868 5.71267 30.238 5.71267H41.0744V4.22266H30.238C27.9646 4.22266 26.9062 5.05863 26.9062 6.85034V13.2388ZM31.5944 9.47572H37.3208V7.9857H31.5944V9.47572Z" fill="white" />
                        <path d="M54.6734 13.2388H57.2423C57.2827 13.2388 57.3029 13.1927 57.2726 13.1674L52.7587 9.54805C52.7284 9.52272 52.7461 9.47668 52.789 9.47668H54.4966C56.7699 9.47668 57.8283 8.64091 57.8283 6.84967C57.8283 5.05842 56.7699 4.22266 54.4966 4.22266H43.7056C43.6804 4.22266 43.6602 4.24108 43.6602 4.2641V13.1973C43.6602 13.2203 43.6804 13.2388 43.7056 13.2388H45.59C45.6153 13.2388 45.6355 13.2203 45.6355 13.1973V5.75604C45.6355 5.73301 45.6557 5.71459 45.6809 5.71459H54.4991C55.3503 5.71459 55.8581 6.13823 55.8581 6.84967C55.8581 7.5611 55.3503 7.98474 54.4991 7.98474H48.4696C48.3433 7.98474 48.2852 8.12748 48.3787 8.20116L54.6431 13.2249C54.6532 13.2342 54.6633 13.2388 54.6734 13.2388Z" fill="white" />
                        <path d="M69.2999 15.1708H71.2727V13.6061H71.2348C73.5082 13.6061 74.5666 12.7363 74.5666 10.872V6.95669C74.5666 5.09247 73.5082 4.22266 71.2348 4.22266H63.7302C61.4568 4.22266 60.3984 5.09247 60.3984 6.95669V10.872C60.3984 12.7363 61.4568 13.6061 63.7302 13.6061H69.2974V15.1708H69.2999ZM71.2374 12.0534H71.2753V10.4863H69.3025V12.0534H63.7352C62.884 12.0534 62.3763 11.6125 62.3763 10.872V6.95669C62.3763 6.21627 62.884 5.77538 63.7352 5.77538H71.2399C72.0911 5.77538 72.5989 6.21627 72.5989 6.95669V10.872C72.5938 11.6125 72.0861 12.0534 71.2374 12.0534Z" fill="white" />
                        <path d="M82.5165 13.2388H84.669V5.95262C84.669 5.81902 84.7985 5.71305 84.9556 5.71305H91.3195V4.22266H75.8633V5.71305H82.2822C82.4118 5.71305 82.5165 5.80059 82.5165 5.90886V13.2388Z" fill="white" />
                        <path d="M105.351 13.2388H107.414V6.84967C107.414 5.05842 106.307 4.22266 103.931 4.22266H96.0848C93.7081 4.22266 92.6016 5.05842 92.6016 6.84967V13.2388H94.664V6.84967C94.664 6.13823 95.1948 5.71459 96.0848 5.71459H103.931C104.82 5.71459 105.351 6.13823 105.351 6.84967V8.73761H97.5055V10.2273H104.91C105.153 10.2273 105.351 10.3999 105.351 10.6117V13.2388Z" fill="white" />
                        <path d="M109.984 4.22266V10.6117C109.984 12.403 111.091 13.2388 113.468 13.2388H124.797V11.7491H113.468C112.578 11.7491 112.047 11.3255 112.047 10.614V4.22266H109.984Z" fill="white" />
                    </svg>
                </div>

                {/* Content */}
                <div className="processor-content">
                    <div className="info-card">
                        {/* Token/Product info */}
                        <div className="token-info">
                            {linkData.img ? (
                                <img src={linkData.img} alt="" style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover' }} />
                            ) : (
                                <div className="token-icon" data-letter={linkData.name?.charAt(0) || 'F'}></div>
                            )}

                            <div className="token-details">
                                <div className="token-header">
                                    <h3 className="token-name">{linkData.name}</h3>
                                    <span className="token-label">TOKEN</span>
                                </div>
                                <div className="token-meta">
                                    <div className="meta-row">
                                        <span className="meta-label">Creator:</span>
                                        <div className="meta-value">
                                            <span className="creator-avatar"></span>
                                            <span className="creator-name">{linkData.creator?.name || linkData.creator?.nick || 'Colonny User'}</span>
                                        </div>
                                    </div>
                                    {linkData.description && (
                                        <div className="meta-row">
                                            <span className="meta-label" style={{ minWidth: 'auto', letterSpacing: 0 }}>{linkData.description}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="info-divider"></div>

                        {/* Price */}
                        <div className="price-section">
                            <div className="price-info">
                                <p className="price-label">${Number(linkData.price).toFixed(2)} <span className="price-currency">{linkData.currency === 'USD' ? 'Dólares' : 'Pesos (MXN)'}</span></p>
                                <p className="price-unit">1 {linkData.name?.substring(0, 3).toUpperCase()}</p>
                            </div>
                        </div>
                    </div>

                    {/* Payment method buttons */}
                    <div className="payment-links">

                        {/* Kushki */}
                        <button className="crypto-btn" data-tooltip="Pagar con Kushki" onClick={() => setIsKushkiModalOpen(true)}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="2" y="5" width="20" height="14" rx="2" stroke="white" strokeWidth="1.5" />
                                <path d="M2 10H22" stroke="white" strokeWidth="1.5" />
                                <rect x="5" y="14" width="4" height="2" rx="0.5" fill="white" />
                            </svg>
                        </button>

                        {/* TRX */}
                        <button className="crypto-btn" data-tooltip="Comprar con TRX">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                <path d="M21.2 7.1L17.5 3.5C17.1 3.1 16.6 3 16.1 3.2L3.2 8C2.6 8.2 2.3 8.8 2.5 9.4L6 20.6C6.2 21.2 6.8 21.5 7.4 21.3L20.3 16.5C20.9 16.3 21.2 15.7 21 15.1L21.2 7.1Z" fill="#EF0027" />
                                <path d="M12 7L8 10L12 17L16 10L12 7Z" fill="white" opacity="0.9" />
                            </svg>
                        </button>

                        {/* USDT TRC20 */}
                        <button className="crypto-btn" data-tooltip="Comprar con USDT">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="11" fill="#26A17B" />
                                <path d="M13.5 7H10.5V9H7V11H17V9H13.5V7ZM12 13C9.2 13 7 12.5 7 11.8V13.2C7 13.9 9.2 14.5 12 14.5C14.8 14.5 17 13.9 17 13.2V11.8C17 12.5 14.8 13 12 13ZM12 15C9.2 15 7 14.5 7 13.8V15.2C7 15.9 9.2 16.5 12 16.5C14.8 16.5 17 15.9 17 15.2V13.8C17 14.5 14.8 15 12 15Z" fill="white" />
                            </svg>
                            <span className="crypto-badge crypto-badge--tron">TRC20</span>
                        </button>

                        {/* USDT ERC20 */}
                        <button className="crypto-btn" data-tooltip="Comprar con USDT">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="11" fill="#26A17B" />
                                <path d="M13.5 7H10.5V9H7V11H17V9H13.5V7ZM12 13C9.2 13 7 12.5 7 11.8V13.2C7 13.9 9.2 14.5 12 14.5C14.8 14.5 17 13.9 17 13.2V11.8C17 12.5 14.8 13 12 13ZM12 15C9.2 15 7 14.5 7 13.8V15.2C7 15.9 9.2 16.5 12 16.5C14.8 16.5 17 15.9 17 15.2V13.8C17 14.5 14.8 15 12 15Z" fill="white" />
                            </svg>
                            <span className="crypto-badge crypto-badge--eth">ERC20</span>
                        </button>

                        {/* AVAX */}
                        <button className="crypto-btn" data-tooltip="Comprar con AVAX">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="11" fill="#E84142" />
                                <path d="M14.8 15.5H17.3L13 8.5C12.8 8.2 12.4 8 12 8C11.6 8 11.2 8.2 11 8.5L6.7 15.5H9.2L12 11L14.8 15.5Z" fill="white" />
                                <path d="M15.5 13.5L16.5 15.5H13.5L14.5 13.5H15.5Z" fill="white" opacity="0.7" />
                            </svg>
                        </button>

                        {/* SOL */}
                        <button className="crypto-btn" data-tooltip="Comprar con SOL">
                            <svg width="22" height="19" viewBox="0 0 101 88" fill="none">
                                <path d="M100.48 69.38L83.81 86.8C83.44 87.18 83.01 87.48 82.52 87.69C82.03 87.89 81.51 88 80.97 88H1.94C1.56 88 1.19 87.89 0.87 87.69C0.56 87.49 0.31 87.2 0.16 86.87C0.01 86.53 -0.04 86.16 0.03 85.79C0.09 85.43 0.26 85.1 0.52 84.83L17.21 67.41C17.57 67.03 18.00 66.73 18.49 66.52C18.98 66.32 19.50 66.21 20.03 66.21H99.06C99.44 66.21 99.81 66.32 100.13 66.52C100.44 66.72 100.69 67.01 100.84 67.34C100.99 67.68 101.04 68.05 100.97 68.42C100.91 68.78 100.74 69.11 100.48 69.38ZM83.81 34.30C83.44 33.92 83.01 33.62 82.52 33.42C82.03 33.21 81.51 33.10 80.97 33.10H1.94C1.56 33.10 1.19 33.21 0.87 33.41C0.56 33.62 0.31 33.90 0.16 34.24C0.01 34.58 -0.04 34.95 0.03 35.31C0.09 35.67 0.26 36.01 0.52 36.28L17.21 53.70C17.57 54.07 18.00 54.38 18.49 54.58C18.98 54.79 19.50 54.89 20.03 54.90H99.06C99.44 54.90 99.81 54.79 100.13 54.59C100.44 54.38 100.69 54.10 100.84 53.76C100.99 53.42 101.04 53.05 100.97 52.69C100.91 52.33 100.74 51.99 100.48 51.72L83.81 34.30ZM1.94 21.79H80.97C81.51 21.79 82.03 21.68 82.52 21.48C83.01 21.27 83.44 20.97 83.81 20.59L100.48 3.17C100.74 2.90 100.91 2.57 100.97 2.21C101.04 1.84 100.99 1.47 100.84 1.13C100.69 0.80 100.44 0.51 100.13 0.31C99.81 0.11 99.44 0 99.06 0H20.03C19.50 0 18.98 0.11 18.49 0.31C18.00 0.52 17.57 0.82 17.21 1.20L0.52 18.62C0.27 18.89 0.10 19.22 0.03 19.58C-0.03 19.95 0.01 20.32 0.16 20.65C0.31 20.99 0.56 21.28 0.88 21.48C1.19 21.68 1.56 21.79 1.94 21.79Z" fill="url(#solgrad)" />
                                <defs>
                                    <linearGradient id="solgrad" x1="8.5" y1="90" x2="89" y2="-3" gradientUnits="userSpaceOnUse">
                                        <stop offset="0.08" stopColor="#9945FF" />
                                        <stop offset="0.5" stopColor="#43B4CA" />
                                        <stop offset="0.97" stopColor="#19FB9B" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </button>

                    </div>
                </div>

                {/* Footer */}
                <div className="processor-footer">
                    <button className="status-link-btn">Ver estado de mi compra</button>
                </div>

            </div>

            {copied && <div className="toast show">Dirección copiada</div>}

            {/* Kushki Checkout Modal Overlay */}
            {isKushkiModalOpen && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.8)', padding: '16px' }}>
                    <div className="payment-processor" style={{ width: '100%', maxWidth: '380px', animation: 'cardIn 0.3s ease-out' }}>
                        <div className="processor-header" style={{ justifyContent: 'space-between' }}>
                            <span style={{ color: '#fff', fontSize: '13px', fontWeight: '500' }}>Terminal Seguro de Kushki</span>
                            <button onClick={() => setIsKushkiModalOpen(false)} style={{ background: 'none', border: 'none', color: '#666680', cursor: 'pointer', fontSize: '18px' }}>&times;</button>
                        </div>
                        <div className="processor-content">
                            {kushkiSuccess ? (
                                <div style={{ textAlign: 'center', padding: '30px 0', color: '#4fd9b3' }}>
                                    <h2 style={{ fontSize: '24px', margin: '0 0 10px' }}>✓ Pagado con éxito</h2>
                                    <p style={{ fontSize: '12px', color: '#666680', margin: 0 }}>Transacción confirmada por Kushki</p>
                                </div>
                            ) : (
                                <form onSubmit={handleKushkiSubmit}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                                        <input type="text" placeholder="Nombre en tarjeta" required className="kushki-input" value={payForm.name} onChange={e => setPayForm({ ...payForm, name: e.target.value })} />
                                        <input type="email" placeholder="Correo electrónico" required className="kushki-input" value={payForm.email} onChange={e => setPayForm({ ...payForm, email: e.target.value })} />
                                        <input type="text" placeholder="Número de Tarjeta (xxxx-xxxx...)" required className="kushki-input" value={payForm.card} onChange={e => setPayForm({ ...payForm, card: e.target.value })} />
                                        <div style={{ display: 'flex', gap: '12px' }}>
                                            <input type="text" placeholder="MM/AA" required className="kushki-input" style={{ flex: 1 }} value={payForm.expiry} onChange={e => setPayForm({ ...payForm, expiry: e.target.value })} />
                                            <input type="text" placeholder="CVC" required className="kushki-input" style={{ flex: 1 }} value={payForm.cvc} onChange={e => setPayForm({ ...payForm, cvc: e.target.value })} />
                                        </div>
                                    </div>
                                    <button type="submit" disabled={kushkiProcessing} style={{ width: '100%', background: '#7c6cfc', color: '#fff', border: 'none', borderRadius: '10px', padding: '12px', fontWeight: '600', cursor: kushkiProcessing ? 'not-allowed' : 'pointer', fontSize: '14px', transition: 'background 0.2s' }}>
                                        {kushkiProcessing ? 'Procesando en UAT...' : `Pagar ${Number(linkData.price).toFixed(2)} ${linkData.currency}`}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PayView;
